import { useMemo, useState } from 'react';
import { Menu, ChevronDown, LogOut, Calendar, Check, Loader2, Bell, CheckCheck, X, ThumbsUp, ThumbsDown, HelpCircle } from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';
import { usePcfNotifications } from '@/hooks/usePcfNotifications';
import { usePushedNotifications } from '@/stores/pushedNotifications';
import { resetMenuTour } from '@/components/shell-ui/MenuTour';
import { getMenuInfoForPath } from '@/config/menuInfo';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { useReadOnly } from '@/hooks/useReadOnly';
import { useFinancialYear } from '@/contexts/FinancialYearContext';
import { useLayout } from '@/contexts/LayoutContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface NotificationItem {
  id: string;
  type: string;
  title: string;
  message?: string;
  isRead: boolean;
  createdAt: string;
  link?: string;
  metadata?: {
    answerId?: string;
    tempAssignmentId?: string;
    questionId?: string;
    currentDueDate?: string;
    requestedDueDate?: string;
    reason?: string;
    assigneeId?: string;
    [k: string]: any;
  } | null;
}

const EXTENSION_REQUEST_TYPE = 'due_date_extension_requested';

interface HeaderProps {
  onMenuClick: () => void;
  /** If true, render even when layout context says layout exists (used by AppLayout) */
  forceRender?: boolean;
  sidebarCollapsed?: boolean;
}

const Header = ({ onMenuClick, forceRender = false, sidebarCollapsed = false }: HeaderProps) => {
  const { hasLayout } = useLayout();
  const { user, logout } = useAuth();
  const readOnly = useReadOnly();

  // ── Time-of-day greeting ────────────────────────────────────────────
  const hour = new Date().getHours();
  const greeting =
    hour < 5 ? 'Good night' :
    hour < 12 ? 'Good morning' :
    hour < 17 ? 'Good afternoon' :
    hour < 21 ? 'Good evening' :
    'Good night';
  const greetingIcon =
    hour < 5 ? '🌙' :
    hour < 12 ? '☀️' :
    hour < 17 ? '🌤️' :
    hour < 21 ? '🌆' :
    '🌙';
  const firstName = (user as any)?.firstName || (user as any)?.first_name || '';
  const displayName = firstName ? firstName.charAt(0).toUpperCase() + firstName.slice(1) : '';
  const navigate = useNavigate();
  const {
    financialYears,
    selectedFinancialYear,
    setSelectedFinancialYear,
    startMonthName,
    loading: fyLoading,
  } = useFinancialYear();


  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // ── Notifications ─────────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const { data: unreadCountResp } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => apiClient.get<{ data: { count: number } }>('/notifications/unread-count'),
    refetchInterval: 60000, // poll every minute
    staleTime: 30000,
  });
  const shellUnread = (unreadCountResp?.data as any)?.count ?? 0;

  const { data: notificationsResp, refetch: refetchNotifications } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => apiClient.get<{ data: NotificationItem[] }>('/notifications?limit=20'),
    enabled: false, // fetched on first open
  });
  const shellNotifications: NotificationItem[] =
    ((notificationsResp?.data as any) ?? []) as NotificationItem[];

  // Merge PCF-derived notifications (polled from services/pcf every 60s).
  // Kept as a hook so there's exactly one bell in the header.
  const pcf = usePcfNotifications();

  // Pushed notifications — any MFE can call `window.esgcaffe.notify(...)`
  // (installed by AppLayout) to surface a lightweight signal in the bell.
  const pushed = usePushedNotifications();
  const pushedAsItems: NotificationItem[] = useMemo(
    () =>
      pushed.items.map((n) => ({
        id: n.id,
        type: `pushed:${n.source}`,
        title: n.title,
        message: n.message,
        isRead: n.read,
        createdAt: n.createdAt,
        link: n.href,
        metadata: { source: n.source },
      })),
    [pushed.items],
  );

  const notifications: NotificationItem[] = useMemo(
    () => [...pushedAsItems, ...pcf.items, ...shellNotifications],
    [pushedAsItems, pcf.items, shellNotifications],
  );
  const unreadCount = shellUnread + pcf.unreadCount + pushed.unread;

  // Retake-tour control — resets the completion flag for the current menu.
  const location = useLocation();
  const currentMenuInfo = getMenuInfoForPath(location.pathname);
  const hasTourForRoute = !!currentMenuInfo?.tourSteps?.length;

  const handleNotificationOpen = (open: boolean) => {
    if (open) refetchNotifications();
  };

  const markRead = async (id: string) => {
    try {
      await apiClient.patch(`/notifications/${id}/read`, {});
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      /* swallow — toast will be shown by interceptor on real error */
    }
  };

  const markAllRead = async () => {
    // The bell merges three feeds and only one of them lives in the database.
    // Clear all three, or the badge barely moves: PCF counters are derived
    // from live data (acknowledged per count, in localStorage) and pushed
    // items are MFE-side signals the shell backend has never heard of.
    pcf.markAllRead();
    pushed.markAllRead();
    try {
      await apiClient.patch('/notifications/read-all', {});
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      // Don't fail silently — the two local feeds are already cleared, so
      // without this the user sees a partial result and no reason for it.
      toast.error('Some notifications could not be marked as read. Please try again.');
    }
  };

  // ── Extension-request approval modal ───────────────────────────────────
  const [extensionModalNotif, setExtensionModalNotif] = useState<NotificationItem | null>(null);
  const [decisionNote, setDecisionNote] = useState('');
  const [submittingDecision, setSubmittingDecision] = useState<null | 'approved' | 'rejected'>(null);

  const handleNotificationClick = (n: NotificationItem) => {
    // PCF-synthetic and MFE-pushed notifications aren't persisted in the
    // shell backend — skip the mark-read PATCH (it would 404). Pushed items
    // are marked read locally via their own store.
    const source = (n.metadata as any)?.source;
    const isPcf = source === 'pcf';
    const isPushed = typeof n.type === 'string' && n.type.startsWith('pushed:');
    if (!n.isRead) {
      if (isPushed) pushed.markRead(n.id);
      else if (isPcf) pcf.markRead(n.id);
      else markRead(n.id);
    }
    if (n.type === EXTENSION_REQUEST_TYPE && n.metadata?.answerId) {
      setExtensionModalNotif(n);
      setDecisionNote('');
      return;
    }
    if (n.link) navigate(n.link);
  };

  const submitExtensionDecision = async (decision: 'approved' | 'rejected') => {
    if (readOnly) return;
    if (!extensionModalNotif?.metadata?.answerId) return;
    setSubmittingDecision(decision);
    try {
      await apiClient.post(
        `/question-assignments/${extensionModalNotif.metadata.answerId}/extension-decision`,
        { decision, note: decisionNote.trim() || undefined }
      );
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['myAssignments'] });
      queryClient.invalidateQueries({ queryKey: ['allAssignments'] });
      setExtensionModalNotif(null);
      setDecisionNote('');
    } finally {
      setSubmittingDecision(null);
    }
  };

  // If layout is already provided by AppLayout and we're not forcing render, skip
  // This prevents duplicate headers when pages still have old layout code
  if (hasLayout && !forceRender) {
    return null;
  }

  return (
    <header data-theme-part="top-bar" className={`fixed top-0 right-0 left-0 ${sidebarCollapsed ? 'lg:left-[72px]' : 'lg:left-[260px]'} z-50 h-16 bg-white/80 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200/50 dark:border-slate-700 flex items-center justify-between px-6 transition-[left] duration-300`}>
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMenuClick}
          className="lg:hidden w-10 h-10 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:border-blue-300 hover:text-blue-600 transition-all"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Time-of-day greeting with an animated icon. Hidden on mobile
             so we don't crowd the top bar; the emoji swaps by hour. */}
        {displayName && (
          <div className="hidden md:flex items-center gap-2 select-none">
            <span
              className="greeting-icon text-2xl leading-none"
              role="img"
              aria-label={greeting.toLowerCase()}
              title={greeting}
            >
              {greetingIcon}
            </span>
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                {greeting},
              </span>
              <span className="text-sm font-semibold text-slate-800 dark:text-white">
                {displayName}
                <span className="text-blue-500"> 👋</span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2">
        {/* Global search / command-palette trigger. Dispatches the Ctrl+K
             keydown so any MFE that has mounted a CommandPaletteProvider
             (e.g. PCF) opens its palette. Silent no-op on modules that
             haven't. */}
        <button
          type="button"
          onClick={() => {
            const isMac = /Mac/i.test(navigator.platform);
            window.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'k',
                code: 'KeyK',
                ctrlKey: !isMac,
                metaKey: isMac,
                bubbles: true,
              }),
            );
          }}
          className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-[12px] text-slate-500 dark:text-slate-300 hover:border-blue-300 hover:bg-white dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1 transition-all"
          title="Search — Ctrl+K / ⌘K"
          aria-label="Open command palette"
        >
          <span>Search…</span>
          <kbd className="font-mono bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-1 text-[10px] text-slate-500 dark:text-slate-400">
            Ctrl K
          </kbd>
        </button>

        {/* Retake-tour — visible only on menus that have a tour registered.
            Clears the localStorage completion flag and reloads so the tour
            replays. */}
        {hasTourForRoute && (
          <button
            type="button"
            onClick={() => resetMenuTour(location.pathname)}
            className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 hover:text-blue-600 hover:border-blue-300 hover:bg-white dark:hover:bg-slate-700 transition"
            title={`Replay ${currentMenuInfo!.title} tour`}
            aria-label={`Replay ${currentMenuInfo!.title} tour`}
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        )}

        {/* Financial Year Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="hidden sm:flex items-center gap-2 px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-blue-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all">
              <Calendar className="w-4 h-4 text-slate-400" />
              <div className="hidden lg:flex flex-col items-start">
                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">Period</span>
                {fyLoading ? (
                  <span className="text-sm font-medium text-slate-700 dark:text-white flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading...
                  </span>
                ) : (
                  <span className="text-sm font-medium text-slate-700 dark:text-white">
                    {selectedFinancialYear ? `FY ${selectedFinancialYear.label}` : 'Select FY'}
                  </span>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Financial Year
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {financialYears.length === 0 ? (
              <DropdownMenuItem disabled>
                <span className="text-slate-400">No financial years assigned</span>
              </DropdownMenuItem>
            ) : (
              financialYears.map((fy) => (
                <DropdownMenuItem
                  key={fy.id}
                  onClick={() => {
                    // No-op if the same FY is re-selected.
                    if (fy.id === selectedFinancialYear?.id) return;
                    // Persist the new selection (written to localStorage + the
                    // shared FY store synchronously), then re-bootstrap the app.
                    // A full reload is the reliable way to make EVERY module
                    // refetch its data scoped to the new financial year: pages
                    // mix React Query (module-scoped client) and useEffect
                    // fetches, so a soft remount alone would leave some cached.
                    // After reload each MFE reads the new FY from the store.
                    setSelectedFinancialYear(fy);
                    window.location.reload();
                  }}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span>FY {fy.label}</span>
                  <div className="flex items-center gap-2">
                    {selectedFinancialYear?.id === fy.id && (
                      <Check className="w-4 h-4 text-blue-500" />
                    )}
                  </div>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="hidden sm:block w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Notifications */}
        <DropdownMenu onOpenChange={handleNotificationOpen}>
          <DropdownMenuTrigger asChild>
            <button
              className="relative w-10 h-10 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:border-blue-300 hover:text-blue-600 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
              title="Notifications"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-semibold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80 max-h-[420px] overflow-hidden flex flex-col">
            <DropdownMenuLabel className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={(e) => { e.preventDefault(); markAllRead(); }}
                  className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="overflow-y-auto" style={{ maxHeight: 340 }}>
              {notifications.length === 0 ? (
                <div className="p-4 text-xs text-slate-400 text-center">No notifications</div>
              ) : (
                notifications.map((n) => (
                  <DropdownMenuItem
                    key={n.id}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNotificationClick(n);
                    }}
                    className={`flex flex-col items-start gap-0.5 py-2 ${!n.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                  >
                    <div className="flex items-center gap-1.5 w-full">
                      <span className="text-sm font-medium flex-1">{n.title}</span>
                      {n.type === EXTENSION_REQUEST_TYPE && (
                        <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Action needed</span>
                      )}
                    </div>
                    {n.message && <span className="text-xs text-slate-500 line-clamp-2">{n.message}</span>}
                    <span className="text-[10px] text-slate-400">{new Date(n.createdAt).toLocaleString()}</span>
                  </DropdownMenuItem>
                ))
              )}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-10 h-10 rounded-lg border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-500 hover:border-red-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
          title="Logout"
        >
          <LogOut className="w-5 h-5" />
        </button>

      </div>

      {/* Extension-request decision modal — opens when the manager clicks a
          "due-date extension requested" notification. */}
      <Dialog
        open={!!extensionModalNotif}
        onOpenChange={(open) => { if (!open) setExtensionModalNotif(null); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              Review Extension Request
            </DialogTitle>
          </DialogHeader>
          {extensionModalNotif && (
            <div className="space-y-3 py-2 text-sm">
              <p className="text-slate-700 dark:text-slate-200">{extensionModalNotif.message}</p>
              <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-md">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Current due date</div>
                  <div className="font-medium">{extensionModalNotif.metadata?.currentDueDate ?? '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400">Requested due date</div>
                  <div className="font-medium text-blue-700 dark:text-blue-400">
                    {extensionModalNotif.metadata?.requestedDueDate ?? '—'}
                  </div>
                </div>
              </div>
              {extensionModalNotif.metadata?.reason && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <div className="text-[10px] uppercase tracking-wide text-amber-700 mb-1">Reason given</div>
                  <div className="text-amber-900">{extensionModalNotif.metadata.reason}</div>
                </div>
              )}
              {!readOnly && (
              <div className="space-y-1">
                <Label htmlFor="decision-note">Note (optional)</Label>
                <Textarea
                  id="decision-note"
                  rows={3}
                  placeholder="Add a comment for the assignee…"
                  value={decisionNote}
                  onChange={(e) => setDecisionNote(e.target.value)}
                />
              </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setExtensionModalNotif(null)}
              disabled={!!submittingDecision}
            >
              <X className="w-4 h-4 mr-1" />
              Close
            </Button>
            {!readOnly && (
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              disabled={!!submittingDecision}
              onClick={() => submitExtensionDecision('rejected')}
            >
              {submittingDecision === 'rejected' ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <ThumbsDown className="w-4 h-4 mr-1" />
              )}
              Reject
            </Button>
            )}
            {!readOnly && (
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              disabled={!!submittingDecision}
              onClick={() => submitExtensionDecision('approved')}
            >
              {submittingDecision === 'approved' ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-1" />
              )}
              Approve
            </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

export default Header;
